import { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * CENTRALIZED AUTH CONTEXT - OPTIMIZED FOR HIGH TRAFFIC
 * 
 * This is the SINGLE SOURCE OF TRUTH for user authentication.
 * All components should use this context instead of reading localStorage directly.
 * 
 * Features:
 * - Session ID to prevent cross-account contamination
 * - Smart retry with exponential backoff
 * - Connection status awareness
 * - Debounced validation to prevent request flooding
 * - Clear error messages in Spanish
 * - Automatic cleanup on logout
 */

const AuthContext = createContext(null);

// Generate unique session ID
const generateSessionId = () => `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

// Error messages in Spanish
const ERROR_MESSAGES = {
  'Invalid credentials': 'Credenciales incorrectas. Verifica tu email y contraseña.',
  'Session expired': 'Tu sesión ha expirado. Por favor inicia sesión nuevamente.',
  'Network Error': 'Error de conexión. Verifica tu internet e intenta de nuevo.',
  'timeout': 'El servidor tardó en responder. Intenta nuevamente.',
  'Too many requests': 'Demasiados intentos. Espera un momento antes de reintentar.',
  'default': 'Error de autenticación. Intenta nuevamente.'
};

// Get user-friendly error message
const getErrorMessage = (error) => {
  if (!error) return ERROR_MESSAGES.default;
  
  // Check for network errors
  if (!navigator.onLine) return ERROR_MESSAGES['Network Error'];
  if (error.code === 'ECONNABORTED') return ERROR_MESSAGES['timeout'];
  if (error.message === 'Network Error') return ERROR_MESSAGES['Network Error'];
  
  // Check for API error responses
  const apiMessage = error.response?.data?.detail;
  if (apiMessage && ERROR_MESSAGES[apiMessage]) return ERROR_MESSAGES[apiMessage];
  if (apiMessage) return apiMessage;
  
  // Check for status codes
  if (error.response?.status === 401) return ERROR_MESSAGES['Invalid credentials'];
  if (error.response?.status === 429) return ERROR_MESSAGES['Too many requests'];
  if (error.response?.status >= 500) return 'Error del servidor. Intenta en unos segundos.';
  
  return ERROR_MESSAGES.default;
};

// Simple axios instance - NO interceptors to avoid session mixing
const authAxios = axios.create({
  baseURL: API_BASE_URL,
  timeout: 15000,
});

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sessionId, setSessionId] = useState(null);
  const [authError, setAuthError] = useState(null);
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [retryCount, setRetryCount] = useState(0);
  const lastValidationRef = useRef(0);
  const validationInProgressRef = useRef(false);

  // Monitor online/offline status
  useEffect(() => {
    const handleOnline = () => {
      setIsOnline(true);
      setAuthError(null);
    };
    const handleOffline = () => {
      setIsOnline(false);
      setAuthError('Sin conexión a internet');
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  // Validate session with server (debounced)
  const validateSession = useCallback(async (forceRefresh = false) => {
    const token = localStorage.getItem('token');
    const storedSessionId = localStorage.getItem('sessionId');
    
    if (!token) {
      setUser(null);
      setLoading(false);
      return null;
    }

    // Debounce: prevent multiple simultaneous validations
    const now = Date.now();
    if (!forceRefresh && validationInProgressRef.current) {
      return user; // Return current user while validation is in progress
    }
    if (!forceRefresh && (now - lastValidationRef.current) < 2000) {
      return user; // Don't validate more than once every 2 seconds
    }

    validationInProgressRef.current = true;
    lastValidationRef.current = now;

    try {
      const response = await authAxios.get('/api/auth/me', {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const freshUser = response.data;
      
      // Update localStorage with fresh data
      localStorage.setItem('user', JSON.stringify(freshUser));
      
      // Ensure session ID exists
      if (!storedSessionId) {
        const newSessionId = generateSessionId();
        localStorage.setItem('sessionId', newSessionId);
        setSessionId(newSessionId);
      } else {
        setSessionId(storedSessionId);
      }
      
      setUser(freshUser);
      setAuthError(null);
      setRetryCount(0);
      return freshUser;
    } catch (error) {
      console.error('Session validation failed:', error);
      
      // CRITICAL: On 401, clear EVERYTHING to prevent contamination
      if (error.response?.status === 401) {
        localStorage.clear();
        setUser(null);
        setSessionId(null);
        setAuthError('Tu sesión ha expirado. Inicia sesión nuevamente.');
        // Redirigir al login - token expirado o inválido
        if (!window.location.pathname.includes('/login') && !window.location.pathname.includes('/signup')) {
          window.location.replace('/login');
        }
      } else {
        // Network error - keep user logged in with cached data
        const cachedUser = localStorage.getItem('user');
        if (cachedUser) {
          try {
            const parsed = JSON.parse(cachedUser);
            setUser(parsed);
            setAuthError('Conexión limitada. Usando datos guardados.');
            return parsed;
          } catch (e) {
            // Invalid cached data
          }
        }
        setAuthError(getErrorMessage(error));
      }
      return null;
    } finally {
      setLoading(false);
      validationInProgressRef.current = false;
    }
  }, [user]);

  // Login function with smart retry
  const login = useCallback(async (email, password, attemptNumber = 1) => {
    const maxAttempts = 3;
    
    try {
      // CRITICAL FIX: Clear EVERYTHING in localStorage first
      // This prevents any residual data from previous sessions
      localStorage.clear();
      
      // Reset React state
      setUser(null);
      setSessionId(null);
      setAuthError(null);
      
      // Small delay to ensure cleanup is complete
      await new Promise(resolve => setTimeout(resolve, 50));
      
      const response = await authAxios.post('/api/auth/login', {
        email,
        password
      });
      
      const { user: userData, token, access_token } = response.data;
      
      // Use access_token if available, fallback to token for backwards compatibility
      const finalToken = access_token || token;
      
      if (!finalToken || !userData) {
        throw new Error('Invalid response from server');
      }
      
      // Create new session with unique ID
      const newSessionId = generateSessionId();
      
      // Store ONLY what we need in localStorage
      localStorage.setItem('token', finalToken);
      localStorage.setItem('user', JSON.stringify(userData));
      localStorage.setItem('sessionId', newSessionId);
      
      // Update React state
      setUser(userData);
      setSessionId(newSessionId);
      setAuthError(null);
      setRetryCount(0);
      
      
      return { success: true, user: userData };
    } catch (error) {
      console.error(`Login attempt ${attemptNumber} failed:`, error);
      
      // Check if it's a retryable error
      const isNetworkError = !error.response || error.code === 'ECONNABORTED';
      const statusCode = error.response?.status;
      const isServerError = [500, 502, 503].includes(statusCode);
      const isRetryableError = isNetworkError || isServerError;
      const shouldRetry = isRetryableError && attemptNumber < maxAttempts;
      
      if (shouldRetry) {
        // Exponential backoff: 1s, 2s, 4s
        const delay = Math.pow(2, attemptNumber - 1) * 1000;
        await new Promise(resolve => setTimeout(resolve, delay));
        return login(email, password, attemptNumber + 1);
      }
      
      const message = getErrorMessage(error);
      setAuthError(message);
      setRetryCount(attemptNumber);
      
      return { 
        success: false, 
        error: message,
        canRetry: isRetryableError,
        attemptsMade: attemptNumber
      };
    }
  }, []);

  // Login with an already-issued token (used by WhatsApp OTP verify flow).
  // The backend returns { token, user } after successful OTP verification; we
  // just persist it into the same slots as the email/password login.
  const loginWithToken = useCallback((token, userData) => {
    if (!token || !userData) {
      console.error('[Auth] loginWithToken called without token or user');
      return { success: false, error: 'Invalid session data' };
    }
    const newSessionId = generateSessionId();
    localStorage.setItem('token', token);
    localStorage.setItem('user', JSON.stringify(userData));
    localStorage.setItem('sessionId', newSessionId);
    setUser(userData);
    setSessionId(newSessionId);
    setAuthError(null);
    setRetryCount(0);
    return { success: true, user: userData };
  }, []);


  // Logout function - COMPLETE cleanup including httpOnly cookie
  const logout = useCallback(() => {
    // FIX: Call backend logout to clear httpOnly session_token cookie
    // Fire-and-forget: no await, no crash if it fails
    const token = localStorage.getItem('token');
    authAxios.post('/api/auth/logout', {}, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
      withCredentials: true
    }).catch(e => console.warn('[Auth] Backend logout failed (non-critical):', e));
    // Clear localStorage immediately regardless of backend response
    localStorage.clear();
    setUser(null);
    setSessionId(null);
    setAuthError(null);
  }, []);

  // Clear all session data
  const clearSession = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('user');
    localStorage.removeItem('sessionId');
    localStorage.removeItem('viadirecta_theme');
    localStorage.removeItem('viadirecta_notif_dismissed');
    // Clear any other app-specific data
    Object.keys(localStorage).forEach(key => {
      if (key.startsWith('viadirecta_')) {
        localStorage.removeItem(key);
      }
    });
  };

  // Refresh user data from server
  const refreshUser = useCallback(async () => {
    return await validateSession();
  }, [validateSession]);

  // Check if user is a driver
  const isDriver = useCallback(() => {
    if (!user) return false;
    return user.role === 'driver' || user.driver_status === 'active';
  }, [user]);

  // Check if user is admin
  const isAdmin = useCallback(() => {
    if (!user) return false;
    return user.role === 'admin' || user.role === 'super_admin';
  }, [user]);

  // Check if user wants to be a driver (but not yet approved)
  const wantsToBeDriver = useCallback(() => {
    if (!user) return false;
    return user.preferred_view === 'driver' && !isDriver();
  }, [user, isDriver]);

  // Get correct dashboard path for user
  const getDashboardPath = useCallback(() => {
    if (!user) return '/login';
    
    if (isAdmin()) return '/admin';
    if (isDriver()) return '/driver/dashboard';
    if (wantsToBeDriver()) return '/become-driver';
    if (user.preferred_view === 'passenger') {
      // Check if profile is complete
      if (!user.name || !user.birth_date || !user.gender) {
        return '/profile-setup';
      }
      return '/passenger/dashboard';
    }
    return '/select-role';
  }, [user, isAdmin, isDriver, wantsToBeDriver]);

  // Initialize on mount - small delay to avoid race condition with AdminPanel mount
  useEffect(() => {
    const timer = setTimeout(() => validateSession(), 100);
    return () => clearTimeout(timer);
  }, [validateSession]);

  const value = {
    user,
    loading,
    sessionId,
    authError,
    isOnline,
    retryCount,
    login,
    loginWithToken,
    logout,
    refreshUser,
    isDriver,
    isAdmin,
    wantsToBeDriver,
    getDashboardPath,
    isAuthenticated: !!user,
    clearError: () => setAuthError(null)
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

// Custom hook to use auth context
export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

// HOC for protected routes
export function withAuth(Component, options = {}) {
  const { requireDriver = false, requireAdmin = false } = options;
  
  return function ProtectedComponent(props) {
    const { user, loading, isDriver, isAdmin } = useAuth();
    const navigate = useNavigate();
    
    useEffect(() => {
      if (!loading) {
        if (!user) {
          navigate('/login');
        } else if (requireAdmin && !isAdmin()) {
          navigate('/');
        } else if (requireDriver && !isDriver()) {
          navigate('/dashboard');
        }
      }
    }, [user, loading, navigate, isDriver, isAdmin]);
    
    if (loading) {
      return (
        <div className="min-h-screen flex items-center justify-center bg-slate-900">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-amber-500"></div>
        </div>
      );
    }
    
    if (!user) return null;
    
    return <Component {...props} />;
  };
}

export default AuthContext;
